import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Renderable from "Component/Renderable";
import { IPlayerStateQuestData } from "State/Player/IPlayerState";
import QuestHelper from "Utility/QuestHelper";
import { Retrier } from "Utility/async";
import { report } from "report";
import { attemptsToLoadBgTile } from "Constants/Quests";
import CleanupManager from "Utility/CleanupManager";
import { unexpectedValue } from "Utility/Types";
import Game from "Game";
import { MathHelper } from "Utility/MathHelper";
import { applyDefaultButtonAnimation } from "System/UI/UIHelper";
import { IGameState } from "State/Game/IGameState";
import QuestTimer from "System/Timer/Timers/QuestTimer";
import { StartAnimation, StartAnimationClip, StopAnimation } from "System/Animation/AnimationHelper";
import { AnimationClipEvent } from "System/Animation/AnimationClip";
import { PlayerActions } from "State/Player/PlayerActions";
import Armature from "../Armature";
import { QuestItemCooldownIntro } from "System/Animation/Clip/QuestItemCooldownIntro";
import { QuestItemCompleteOutro } from "System/Animation/Clip/QuestItemCompleteOutro";
import { AlphaPulseLoop } from "System/Animation/Clip/AlphaPulseLoop";
import { Alpha } from "System/Animation/Clip/Alpha";
import { ProgressBarSlideMotion } from "System/Animation/Clip/ProgressBarSlideMotion";
import { isArray } from "lodash";
import { QuestItemLayout } from "Layouts/Quests/QuestItemLayout";
import TextComponent from "Component/Render/Text";
import TransformComponent from "Component/Transform";
import SpriteComponent from "Component/Render/Sprite";

export interface IQuestItemData {
    title: string;
    progress: number;
    icon: string;
    rewardIcon: string;
    rewardDesc: string;
}

interface IQuestItemParams {
    questIndex: number;
    layout: QuestItemLayout,
    data: IPlayerStateQuestData,
}

enum QuestStatus {
    ON_COOLDOWN = 'onCooldown',
    LOADING = 'loading',
    LOADED = 'loaded',
}

export const progressBarFillMaxWidth: number = 241;

export default class QuestItem {
    private layout: QuestItemLayout;
    private animating: boolean = false;
    private questIndex: number;
    private tileLoaded: boolean = false;
    private timer: QuestTimer;
    private timerTextComponent: TextComponent;
    private status: QuestStatus;
    private questData: IPlayerStateQuestData;
    private containerCleanupManager: CleanupManager = new CleanupManager();
    private readonly loadTileRetrier: Retrier = new Retrier(
        undefined,
        (error, attemptIndex) => {
            if (attemptIndex === attemptsToLoadBgTile) {
                report(
                    `Failed to load quest background tile ${attemptsToLoadBgTile} times in row`,
                    {
                        error,
                        once: true,
                        sampleRate: 0.1,
                        fingerprint: 'QuestAssetLoadingError',
                        tags: {
                            questType: this.questData?.type,
                            tier: this.questData?.tier,
                        },
                    }
                );
            }
        }
    );

    constructor(params: IQuestItemParams) {
        this.layout = params.layout;
        this.questData = params.data;
        this.questIndex = params.questIndex;
    }

    public get isAnimating(): boolean {
        return this.animating;
    }

    public updateData(
        data: IPlayerStateQuestData,
        state: IGameState = Game.store.getState()
    ) {
        if (!data || this.animating || this.layout.root.destroyed) {
            return;
        }

        const progressChanged = data.progress !== this.questData?.progress;
        this.questData = { ...data };
        const status = this.getStatus(this.questData);
        if (status !== this.status) {
            // Handle change of status
            switch (status) {
                case QuestStatus.ON_COOLDOWN:
                    this.containerCleanupManager.cleanUpAll();

                    const container = this.layout.cooldownContainer;
                    const timerText = this.layout.timerText;

                    this.layout.collectButton.getComponent(Renderable).visible = false;

                    const refresh = GameObject.Instantiate(Armature, {
                        dragonbonesName: 'quest_complete_animations',
                        armatureName: 'next_mission',
                        alpha: 0,
                        playOnStart: false,
                    });
                    this.layout.cooldownDBContainer.addChild(refresh);

                    this.initializeContainer(container);

                    this.timerTextComponent = timerText.getComponent(TextComponent);
                    this.containerCleanupManager.add(() => {
                        this.timerTextComponent = null;
                    });

                    this.timer = new QuestTimer(this.questIndex);
                    this.timer.onUpdate = this.updateTimerText;
                    this.timer.onUpdate(state);
                    this.containerCleanupManager.add(() => {
                        this.timer.stop();
                    });

                    const cooldownText = this.layout.cooldownText;
                    StartAnimationClip(
                        new QuestItemCooldownIntro(container, {
                            cooldownText,
                            timerText,
                            refresh,
                        })
                    );
                    break;
                case QuestStatus.LOADING:
                    // Display loading container
                    const loadingContainer = this.layout.loadingContainer;
                    this.initializeContainer(loadingContainer);

                    // Begin to attempt to load background image
                    this.loadTileRetrier
                        .restart(() => this.loadTile(), false)
                        .then(this.onTileLoaded);
                    break;
                case QuestStatus.LOADED:
                    break;
                default:
                    unexpectedValue(status);
            }
            // Update status
            this.status = status;
        } else if (status === QuestStatus.LOADED) {
            // Update progress
            if (progressChanged) {
                this.updateActiveQuest(this.questData);
            }
        }
    }

    public destroy() {
        this.containerCleanupManager.cleanUpAll();
    }

    private readonly onCollectClicked = () => {
        if (this.animating) return;
        const container = this.layout.eventBG;
        StopAnimation(container);
        const button = this.layout.collectButton;
        const activeContainer = this.layout.activeContainer;
        const completeContainer = this.layout.completedContainer;
        const completeText = this.layout.completeText;
        this.animating = true;

        // Create check animation
        const checkDBContainer = this.layout.completedDBContainer;
        const check = GameObject.Instantiate(Armature, {
            dragonbonesName: 'quest_complete_animations',
            armatureName: 'check',
            alpha: 0,
            playOnStart: false,
        });
        checkDBContainer.addChild(check);

        StartAnimationClip(
            new QuestItemCompleteOutro(container, {
                initialDelay: 750,
                button,
                activeContainer,
                completeContainer,
                completeText,
                check,
            }).once(AnimationClipEvent.DESTROY, () => {
                this.animating = false;
                const state = Game.store.getState();
                this.updateData(
                    state.playerState.quests[this.questIndex],
                    state
                );
            })
        );
        PlayerActions.claimQuestReward(this.questIndex);
    };
    private readonly updateTimerText = (state: IGameState) => {
        if (this.timerTextComponent && this.timerTextComponent.container && this.timer?.getDate(state)) {
            this.timerTextComponent.text = this.timer.getFormattedCountdown(
                state
            );
        }
    };
    public handleStateChanged = (state: IGameState) => {
        this.updateData(state.playerState.quests[this.questIndex], state);
    };
    private readonly onTileLoaded = () => {
        if (this.layout.root.destroyed) return;

        this.tileLoaded = true;
        this.containerCleanupManager.cleanUpAll();
        this.containerCleanupManager.add(() => {
            this.tileLoaded = false;
        });
        const questContainer = this.layout.eventBG;
        this.setupQuest(this.questData);
        this.initializeContainer(questContainer);
        this.updateActiveQuest(this.questData);
    };
    private updateActiveQuest(data: IPlayerStateQuestData) {
        this.setProgress();

        // Set bg visibility
        this.backgroundActive.getComponent(Renderable).visible = true;
        this.backgroundCompleted.getComponent(Renderable).visible = false;

        const canComplete = QuestHelper.canCompleteQuest(data);
        const container = this.layout.eventBG;
        if (canComplete) {
            const button = this.layout.collectButton;
            const borderGlow = this.layout.bgGlow;

            // DB button shine animation
            const buttonDBContainer = this.layout.buttonDBContainer;
            const buttonShine = GameObject.Instantiate(Armature, {
                dragonbonesName: 'quest_complete_animations',
                armatureName: 'button_glare',
            });
            buttonDBContainer.addChild(buttonShine);

            // DB reward shine animaiton
            const rewardDBContainer = this.layout.rewardDBContainer;
            const rewardShine = GameObject.Instantiate(Armature, {
                dragonbonesName: 'quest_complete_animations',
                armatureName: 'reward_rays',
                alpha: 0,
            });
            rewardDBContainer.addChild(rewardShine);
            /*  */
            StartAnimation(container, 'QuestItemCompleteIntro', {
                initialDelay: 750,
                button,
                borderGlow,
                buttonShine,
                rewardShine,
                backgroundCompleted: this.backgroundCompleted,
                backgroundActive: this.backgroundActive,
            }).on(AnimationClipEvent.COMPLETE, () => {
                const progressShiny = this.layout.progressBarFillShiny;
                StartAnimationClip(
                    new ProgressBarSlideMotion(progressShiny, {
                        duration: 500,
                        delay: 1000,
                        progressBarFillMaxWidth,
                    })
                );
            });
        } else {
            StartAnimationClip(
                new AlphaPulseLoop(this.layout.bgShiny, {
                    duration: 500,
                    alpha: [0, 1],
                    delay: 1000,
                })
            );
        }
    }
    private loadTile(): Promise<void> {
        QuestHelper.addTileIfNeeded(this.questData.type, this.questData.tier);
        return Game.resource.load();
    }
    private getStatus(data: IPlayerStateQuestData) {
        if (QuestHelper.isQuestOnCooldown(data)) {
            return QuestStatus.ON_COOLDOWN;
        }
        if (this.tileLoaded) {
            return QuestStatus.LOADED;
        }
        return QuestStatus.LOADING;
    }
    private setupQuest(data: IPlayerStateQuestData) {
        const canComplete = QuestHelper.canCompleteQuest(data);
        const target = QuestHelper.getFinalQuestTarget(
            data.type,
            data.tier,
            QuestHelper.getQuestPoolKeyByEventType(data.eventType)
        );
        const reward = QuestHelper.getQuestRewards(data.tier)[0];
        if (isArray(reward)) {
            report("Multi reward quests are not implemented for standard quests", {
                once: true,
                sampleRate: 0.01,
                tags: {
                    tier: data.tier,
                    type: data.type
                },
                fingerprint: "setupQuest:MultiRewardNotImplemented"
            });
            return;
        }

        // Set quest background
        const container = this.layout.eventBG;
        const questBg = QuestHelper.getBackgroundTileKey(data.type, data.tier);
        container.getComponent(SpriteComponent).texture = questBg;

        // Set quest title
        const questTitle = this.layout.questTitle;
        const questTitleText = Game.locale.getText(QuestHelper.getTitleKey(data.type));
        questTitle.getComponent(TextComponent).text = questTitleText;

        // Set progress text
        const progressBarText = this.layout.progressText;
        const progressText = `${data.progress} / ${target}`;
        progressBarText.getComponent(TextComponent).text = progressText;

        // Set reward sprite
        const rewardSprite = this.layout.rewardSprite;
        rewardSprite.getComponent(SpriteComponent).texture = reward.iconCacheKey;
        rewardSprite.getComponent(TransformComponent).scale.set(reward.iconScale[0]);
        rewardSprite.getComponent(TransformComponent).angle = reward.angle;

        // Set reward text amount
        const rewardText = this.layout.rewardText;
        rewardText.getComponent(TextComponent).text = String(reward.value);

        // Set background glow visibility
        const backgroundGlow = this.layout.bgGlow;
        backgroundGlow.getComponent(Renderable).visible = !canComplete;

        // Set background shiny visibility
        const backgroundShiny = this.layout.bgShiny;
        backgroundShiny.getComponent(Renderable).visible = canComplete;

        // Set Progress bar fill sprite
        const progressBarFill = this.layout.progressBarFill;
        progressBarFill.getComponent(SpriteComponent).texture = canComplete ? 'mission_bar_green' : 'mission_bar_blue';

        const collectButton = this.layout.collectButton;
        applyDefaultButtonAnimation(collectButton);
        collectButton.on(GameObjectEvent.SELECTED, this.onCollectClicked);
        this.containerCleanupManager.add(() => {
            collectButton.off(GameObjectEvent.SELECTED, this.onCollectClicked);
        });
    }

    private initializeContainer(container: GameObject) {
        this.containerCleanupManager.add(() => {
            StartAnimationClip(
                new Alpha(container, {
                    startAlpha: 1,
                    endAlpha: 0,
                    duration: 450,
                }).once(AnimationClipEvent.DESTROY, () => {
                    container.destroy();
                })
            );
        });
    }
    private setProgress() {
        const fill = this.progressBarFill;
        const text = this.progressBarText;
        const target = QuestHelper.getQuestTarget(
            this.questData.type,
            this.questData.tier
        );

        const fillRenderable = fill.getComponent(Renderable);

        if (isArray(target)) {
            // TODO: Implement multistage quests into normal quests
            // (not sure if this class will stay for long)
            console.warn("Multi-stage quests not implemented for non-event quests")
            return;
        }

        // Set bar fill length
        if (this.questData.progress !== 0) {
            fillRenderable.visible = true;
            const percProgress = MathHelper.normalize(
                this.questData.progress,
                0,
                target
            );
            fill.getComponent(TransformComponent).width = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth
            );
        } else {
            fillRenderable.visible = false;
        }
        let currentProgress = MathHelper.clamp(
            this.questData.progress,
            0,
            target
        );
        // Set Progress text
        text.getComponent(
            TextComponent
        ).text = `${currentProgress} / ${target}`;
    }
    private get progressBarFill() {
        return this.layout.progressBarFill;
    }
    private get progressBarText() {
        return this.layout.progressText
    }
    private get backgroundActive() {
        return this.layout.bgActive;
    }
    private get backgroundCompleted() {
        return this.layout.bgCompleted;
    }
    public get activeContainer() {
        return this.layout.activeContainer;
    }
    public get completedContainer() {
        return this.layout.completedContainer;
    }
}
